/**
 * IcpSupplyBreakdown — V2.K.3
 *
 * Renders a supply breakdown for ICP on the IcpMonitor page.
 *
 * Why ICP is its own story (different from HBAR / Cronos / Flux) :
 *  - NO max supply cap — ICP is an uncapped chain. New ICP is minted
 *    via NNS voting rewards + node provider rewards. ICP is burned
 *    when converted to cycles to pay for canister computation /
 *    storage. Net inflation = mint − burn.
 *  - The big "locked" bucket here is the ~250 M ICP staked in NNS
 *    neurons (≈ 45 % of circulating) — this is what we surface.
 *  - Mission 70 (DFINITY whitepaper Jan-Feb 2026) targets a cut of
 *    annual inflation from 9.72 % to 2.92 % by end of 2026 via
 *    capped voting rewards, lower node provider rewards, and a new
 *    20 % revenue-based burn.
 *
 * The breakdown surfaces :
 *   - Circulating supply + free float vs NNS-staked split
 *   - Current annual inflation rate + estimated annual mint
 *   - Mission 70 context (informational callout)
 *   - Official ICP Dashboard + Mission 70 whitepaper links
 */

import { useTranslation } from 'react-i18next';
import { useIcpSupply } from '../../hooks/useIcpSupply';

const MISSION_70_TARGET_INFLATION_PCT = 2.92;
const MISSION_70_PRE_REFORM_INFLATION_PCT = 9.72;

// Verified public references. No raw wallet addresses are hardcoded :
// DFINITY treasury / foundation wallets are not labelled in a single
// authoritative source we can verify, and the burn happens at the
// protocol level (ICP → cycles), not via a 0x…dEaD-style address.
const KNOWN_ENDPOINTS: Array<{
  label: string;
  url: string;
  category: 'dashboard' | 'docs' | 'whitepaper';
  note: string;
}> = [
  {
    label: 'ICP Dashboard — Tokenomics',
    url: 'https://dashboard.internetcomputer.org/tokenomics',
    category: 'dashboard',
    note: 'Métriques supply officielles : mint, burn, inflation, voting rewards.',
  },
  {
    label: 'ICP Dashboard — Circulation',
    url: 'https://dashboard.internetcomputer.org/circulation',
    category: 'dashboard',
    note: 'Breakdown circulant vs locked vs staked.',
  },
  {
    label: 'Mission 70 Whitepaper (DFINITY, janvier 2026)',
    url: 'https://internetcomputer.org/whitepapers/mission70.pdf',
    category: 'whitepaper',
    note: 'Plan officiel de réduction d\'inflation 9,72 % → 2,92 %.',
  },
  {
    label: 'Tokenomics overview (learn.internetcomputer.org)',
    url: 'https://learn.internetcomputer.org/hc/en-us/articles/34090810571284-Tokenomics',
    category: 'docs',
    note: 'Mécanique mint / burn / neurones / cycles.',
  },
];

export function IcpSupplyBreakdown() {
  const { t, i18n } = useTranslation();
  const { data, isLoading, error } = useIcpSupply();

  const locale = i18n?.language || 'fr';
  const fmt = (n: number) =>
    n.toLocaleString(locale === 'fr' ? 'fr-FR' : locale, { maximumFractionDigits: 0 });
  const fmtM = (n: number) =>
    `${(n / 1_000_000).toLocaleString(locale === 'fr' ? 'fr-FR' : locale, {
      maximumFractionDigits: 2,
    })} M`;

  return (
    <section
      aria-labelledby="icp-supply-breakdown-title"
      style={{
        background: 'rgba(0, 0, 0, 0.25)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        padding: '20px',
        marginBottom: '20px',
      }}
    >
      <header style={{ marginBottom: '14px' }}>
        <h2
          id="icp-supply-breakdown-title"
          style={{
            fontSize: '16px',
            fontWeight: 700,
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span aria-hidden style={{ color: '#ED1E79' }}>∞</span>
          {t('icp.supply.title', 'Supply ICP — répartition')}
        </h2>
        <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
          {t(
            'icp.supply.subtitle',
            'Pas de cap maximal — l\'émission est compensée par le burn ICP → cycles. Mission 70 vise à inverser le ratio mint/burn d\'ici fin 2026.',
          )}
        </div>
      </header>

      {isLoading ? (
        <SkeletonBlock />
      ) : error || !data ? (
        <ErrorBlock />
      ) : (
        <>
          <StatGrid data={data} fmt={fmt} fmtM={fmtM} t={t} />
          <NeuronSplitBar data={data} t={t} />
          <Legend t={t} />
          <Mission70Note
            t={t}
            fmt={fmt}
            currentInflation={data.annualInflationPct}
            preReform={MISSION_70_PRE_REFORM_INFLATION_PCT}
            target={MISSION_70_TARGET_INFLATION_PCT}
            annualMint={data.annualMint}
          />
          <KnownEndpoints t={t} />
        </>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Stat grid
// ─────────────────────────────────────────────────────────────────────

interface StatGridProps {
  data: NonNullable<ReturnType<typeof useIcpSupply>['data']>;
  fmt: (n: number) => string;
  fmtM: (n: number) => string;
  t: (key: string, fallback: string) => string;
}

function StatGrid({ data, fmt, fmtM, t }: StatGridProps) {
  const freeFloat = Math.max(0, data.circulating - data.nnsStakedReference);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '12px',
        marginBottom: '16px',
      }}
    >
      <StatCard
        label={t('icp.supply.circulating', 'En circulation')}
        value={fmtM(data.circulating)}
        sub={`${fmt(data.circulating)} ICP`}
        color="#ED1E79"
      />
      <StatCard
        label={t('icp.supply.nnsStaked', 'Verrouillé dans NNS (neurons)')}
        value={fmtM(data.nnsStakedReference)}
        sub={`~${data.nnsStakedPctOfCirculating.toFixed(0)} % de la supply en circulation`}
        color="#522785"
      />
      <StatCard
        label={t('icp.supply.freeFloat', 'Free float estimé')}
        value={fmtM(freeFloat)}
        sub={t('icp.supply.freeFloatNote', 'Circulating moins ICP en neurons')}
        color="#10b981"
      />
      <StatCard
        label={t('icp.supply.inflation', 'Inflation annuelle')}
        value={`${data.annualInflationPct.toFixed(2)} %`}
        sub={`~${fmtM(data.annualMint)} ICP / an (mint estimé)`}
        color="#f59e0b"
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub: string;
  color: string;
}) {
  return (
    <div
      style={{
        background: `${color}10`,
        border: `1px solid ${color}30`,
        borderRadius: '10px',
        padding: '12px 14px',
      }}
    >
      <div
        style={{
          fontSize: '11px',
          color: 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '20px',
          fontWeight: 700,
          color: 'var(--text)',
          marginTop: '4px',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: '11px',
          color: 'var(--muted)',
          marginTop: '2px',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {sub}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Free float vs NNS-staked split (no "remaining to mint" — uncapped)
// ─────────────────────────────────────────────────────────────────────

function NeuronSplitBar({
  data,
  t,
}: {
  data: NonNullable<ReturnType<typeof useIcpSupply>['data']>;
  t: (key: string, fallback: string) => string;
}) {
  const stakedPct = Math.min(100, data.nnsStakedPctOfCirculating);
  const freeFloatPct = Math.max(0, 100 - stakedPct);
  return (
    <div
      style={{ marginBottom: '8px' }}
      aria-label={t(
        'icp.supply.barAlt',
        'Répartition de la supply ICP en circulation : free float vs ICP verrouillé dans des neurons NNS',
      )}
      role="img"
    >
      <div
        style={{
          display: 'flex',
          height: '24px',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid var(--border)',
        }}
      >
        <div
          style={{
            width: `${freeFloatPct}%`,
            background: 'linear-gradient(90deg, #10b981, #34d399)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '11px',
            fontWeight: 700,
            transition: 'width 0.5s ease',
          }}
          title={`${freeFloatPct.toFixed(1)} %`}
        >
          {freeFloatPct > 8 ? `${freeFloatPct.toFixed(0)} %` : ''}
        </div>
        <div
          style={{
            width: `${stakedPct}%`,
            background: 'linear-gradient(90deg, #522785, #ED1E79)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '11px',
            fontWeight: 700,
            transition: 'width 0.5s ease',
          }}
          title={`${stakedPct.toFixed(1)} %`}
        >
          {stakedPct > 8 ? `${stakedPct.toFixed(0)} %` : ''}
        </div>
      </div>
    </div>
  );
}

function Legend({ t }: { t: (key: string, fallback: string) => string }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: '14px',
        flexWrap: 'wrap',
        fontSize: '11px',
        color: 'var(--muted)',
        marginBottom: '14px',
      }}
    >
      <LegendItem color="#10b981" label={t('icp.supply.legendFloat', 'Free float (tradable)')} />
      <LegendItem
        color="#ED1E79"
        label={t('icp.supply.legendStaked', 'Verrouillé dans neurons NNS (~250 M ICP)')}
      />
    </div>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <span
        aria-hidden
        style={{
          width: '10px',
          height: '10px',
          borderRadius: '3px',
          background: color,
          display: 'inline-block',
        }}
      />
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Mission 70 callout
// ─────────────────────────────────────────────────────────────────────

function Mission70Note({
  t,
  fmt,
  currentInflation,
  preReform,
  target,
  annualMint,
}: {
  t: (key: string, fallback: string) => string;
  fmt: (n: number) => string;
  currentInflation: number;
  preReform: number;
  target: number;
  annualMint: number;
}) {
  // Visual progress between pre-reform inflation and the Mission 70
  // target. Clamped so the bar stays readable even if currentInflation
  // moves outside [target, preReform].
  const span = Math.max(preReform - target, 0.01);
  const progressPct = Math.max(
    0,
    Math.min(100, ((preReform - currentInflation) / span) * 100),
  );

  const body = t(
    'icp.supply.mission70Body',
    "DFINITY a publié le whitepaper Mission 70 en janvier 2026 — objectif : ramener l'inflation annuelle de {{pre}} % à {{target}} % d'ici fin 2026. Mécaniques : plafonnement des voting rewards NNS, baisse des rewards node providers, nouveau burn de 20 % du revenu cycles, modulation de maturité simplifiée. Avec ~{{mint}} ICP minté par an au taux actuel ({{current}} %), atteindre la cible Mission 70 réduirait fortement la pression haussière sur la supply — voire la rendrait déflationniste si le burn cycles dépasse le mint.",
  )
    .replace('{{pre}}', preReform.toFixed(2))
    .replace('{{target}}', target.toFixed(2))
    .replace('{{current}}', currentInflation.toFixed(2))
    .replace('{{mint}}', fmt(annualMint));

  return (
    <div
      style={{
        padding: '12px 14px',
        background: 'rgba(237, 30, 121, 0.06)',
        border: '1px dashed rgba(237, 30, 121, 0.35)',
        borderRadius: '10px',
        fontSize: '12px',
        color: 'var(--text)',
        lineHeight: 1.6,
        marginBottom: '14px',
      }}
    >
      <strong style={{ color: '#ED1E79' }}>
        {t('icp.supply.mission70Title', 'Mission 70 — réduction d\'inflation (DFINITY, janv. 2026)')}
      </strong>
      <div style={{ marginTop: '4px' }}>{body}</div>

      {/* Progress bar : pre-reform → current → target */}
      <div style={{ marginTop: '10px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '11px',
            color: 'var(--muted)',
            marginBottom: '4px',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <span>{target.toFixed(2)} % (cible)</span>
          <span>{currentInflation.toFixed(2)} % (actuel)</span>
          <span>{preReform.toFixed(2)} % (avant réforme)</span>
        </div>
        <div
          style={{
            position: 'relative',
            height: '8px',
            background: 'rgba(255, 255, 255, 0.06)',
            borderRadius: '4px',
            overflow: 'hidden',
          }}
          aria-hidden
        >
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${progressPct}%`,
              background: 'linear-gradient(90deg, #ED1E79, #f59e0b)',
              transition: 'width 0.5s ease',
            }}
          />
        </div>
      </div>

      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '6px' }}>
        {t(
          'icp.supply.mission70Source',
          'Source : internetcomputer.org/whitepapers/mission70.pdf · dashboard.internetcomputer.org/tokenomics.',
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Known endpoints
// ─────────────────────────────────────────────────────────────────────

function KnownEndpoints({ t }: { t: (key: string, fallback: string) => string }) {
  return (
    <details
      style={{
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid var(--border)',
        borderRadius: '10px',
        padding: '12px 14px',
      }}
    >
      <summary
        style={{
          fontSize: '13px',
          fontWeight: 600,
          color: 'var(--text)',
          cursor: 'pointer',
          listStyle: 'revert',
        }}
      >
        {t('icp.supply.knownEndpointsTitle', 'Sources officielles & dashboards')}
      </summary>

      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '8px', lineHeight: 1.6 }}>
        {t(
          'icp.supply.knownEndpointsDisclaimer',
          'ICP n\'utilise pas d\'adresse de burn standard : le burn ICP → cycles se fait au niveau protocole, géré par les canisters NNS/ledger. Le ICP Dashboard expose quelques neurons labellisés — notamment le neuron-followee DFINITY Foundation #27, dont la clé privée a été détruite lors d\'une cérémonie publique à la création (dashboard.internetcomputer.org/neuron/27). Pas de wallet trésorerie unique adressable.',
        )}
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0' }}>
        {KNOWN_ENDPOINTS.map((entry) => (
          <li
            key={entry.url}
            style={{
              padding: '10px 0',
              borderTop: '1px solid var(--border)',
              fontSize: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
              <CategoryPill category={entry.category} />
              <a
                href={entry.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--text)', fontWeight: 600, textDecoration: 'none' }}
              >
                {entry.label} ↗
              </a>
            </div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
              {entry.note}
            </div>
          </li>
        ))}
      </ul>
    </details>
  );
}

function CategoryPill({ category }: { category: 'dashboard' | 'docs' | 'whitepaper' }) {
  const styles: Record<typeof category, { bg: string; color: string; label: string }> = {
    dashboard: { bg: 'rgba(237, 30, 121, 0.15)', color: '#ec4899', label: 'Dashboard' },
    docs: { bg: 'rgba(99, 102, 241, 0.15)', color: '#818cf8', label: 'Docs' },
    whitepaper: { bg: 'rgba(82, 39, 133, 0.18)', color: '#a78bfa', label: 'Whitepaper' },
  };
  const s = styles[category];
  return (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 700,
        padding: '2px 6px',
        borderRadius: '4px',
        background: s.bg,
        color: s.color,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
      }}
    >
      {s.label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Loading / error states
// ─────────────────────────────────────────────────────────────────────

function SkeletonBlock() {
  return (
    <div aria-hidden>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              background: 'rgba(255, 255, 255, 0.04)',
              borderRadius: '10px',
              height: '72px',
            }}
          />
        ))}
      </div>
      <div
        style={{
          height: '24px',
          background: 'rgba(255, 255, 255, 0.04)',
          borderRadius: '8px',
          marginBottom: '14px',
        }}
      />
    </div>
  );
}

function ErrorBlock() {
  return (
    <div
      style={{
        padding: '14px',
        background: 'rgba(239, 68, 68, 0.08)',
        border: '1px solid rgba(239, 68, 68, 0.25)',
        borderRadius: '10px',
        fontSize: '12px',
        color: 'var(--muted)',
      }}
    >
      Données CoinGecko indisponibles pour le moment. La supply ICP sera affichée dès que l'API répondra.
    </div>
  );
}
